import { CampaignListCard } from "@/components/campaign/campaign-list-card";
import { CampaignRecord, useCampaigns } from "@/hooks/use-campaigns";
import { useTheme } from "@/hooks/use-theme";
import { CampaignStatus } from "@/services/campaigns.service";
import { useAuthStore } from "@/stores/auth-store";
import { getCampaignStatusLabel } from "@/utils/campaigns";
import {
  Button,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  Spinner,
} from "@getgreenline/blaze-ui";
import { FilterIcon, ListFilter, SearchX } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";

const campaignStatusFilterOptions: CampaignStatus[] = [
  "DRAFT",
  // "SCHEDULED",
  "ACTIVE",
  "PAUSED",
  // "ENDED",
  "ARCHIVED",
];

const campaignStatusFilterDefaultSelection: CampaignStatus[] = [
  "DRAFT",
  "ACTIVE",
  "PAUSED",
];

//Subject to change if more statuses are added or default statuses are changed
function statusFilterHasDefaultSelection(filter: Set<CampaignStatus>) {
  return (
    filter.size === campaignStatusFilterDefaultSelection.length &&
    !filter.has("ARCHIVED")
  );
}

function formatFilterButtonLabel(statusFilter: Set<CampaignStatus>) {
  if (statusFilter.size === campaignStatusFilterOptions.length)
    return "All statuses";

  if (statusFilterHasDefaultSelection(statusFilter)) {
    return "Filter campaigns";
  }

  if (statusFilter.size == 1) return "1 Filter";

  return statusFilter.size + " Filters";
}

function LoadingCampaignsEmptyPlaceholder() {
  return (
    <Empty>
      <EmptyMedia>
        <Spinner className="tw:h-10! tw:w-10!" />
      </EmptyMedia>
      <EmptyHeader className="tw:font-semibold">Loading campaigns</EmptyHeader>
    </Empty>
  );
}

function NoFilterSelectedEmptyPlaceholder({
  clearFilter,
}: {
  clearFilter: () => void;
}) {
  return (
    <Empty>
      <EmptyMedia>
        <ListFilter></ListFilter>
      </EmptyMedia>
      <EmptyHeader className="tw:font-semibold">Select a status</EmptyHeader>
      <EmptyContent>
        Pick at least one status to see your campaigns, or show them all.
        <Button onClick={clearFilter}>Clear filter</Button>
      </EmptyContent>
    </Empty>
  );
}

function NoCampaignsEmptyPlaceholder({
  clearFilter,
}: {
  clearFilter: () => void;
}) {
  return (
    <Empty>
      <EmptyMedia variant="icon">
        <SearchX className="tw:size-6" />
      </EmptyMedia>
      <EmptyHeader className="tw:font-semibold">
        <h1>No campaigns match your filter</h1>
      </EmptyHeader>
      <EmptyContent className="tw:text-muted-foreground">
        No campaigns have the statuses you selected. Try a different status or
        clear the filter to see everything.
        <Button onClick={clearFilter}>Clear filter</Button>
      </EmptyContent>
    </Empty>
  );
}

function CampaignStatusFilterMenuContent({
  checkedStatuses,
  setCheckedStatuses,
}: {
  checkedStatuses: Set<CampaignStatus>;
  setCheckedStatuses: Dispatch<SetStateAction<Set<CampaignStatus>>>;
}) {
  const changeStatus = (status: CampaignStatus, checked: boolean) => {
    const next = new Set(checkedStatuses);
    if (checked) next.add(status);
    else next.delete(status);

    setCheckedStatuses(next);
  };

  return (
    <DropdownMenuContent>
      <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuCheckboxItem
          checked={checkedStatuses.size === campaignStatusFilterOptions.length}
          onCheckedChange={(checked) => {
            if (checked)
              setCheckedStatuses(new Set(campaignStatusFilterOptions));
          }}
        >
          All
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {campaignStatusFilterOptions.map((s) => (
          <DropdownMenuCheckboxItem
            checked={checkedStatuses.has(s)}
            onCheckedChange={(c) => changeStatus(s, c)}
            key={s}
          >
            {getCampaignStatusLabel(s)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuGroup>
    </DropdownMenuContent>
  );
}

function StatusFilterBadges({
  statusFilter,
  clearStatuses,
}: {
  statusFilter: Set<CampaignStatus>;
  clearStatuses: () => void;
}) {
  if (statusFilter.size == 0) return null;
  if (statusFilterHasDefaultSelection(statusFilter)) return null;

  return (
    <div className="tw:flex tw:gap-2 tw:flex-wrap tw:align-item tw:items-center">
      <label>Filter status by:</label>
      {Array.from(statusFilter)
        .sort()
        .map((s) => getCampaignStatusLabel(s))
        .join(", ")}
      <button
        className="tw:cursor-pointer tw:font-medium tw:text-foreground tw:underline tw:underline-offset-2 hover:tw:no-underline"
        onClick={clearStatuses}
      >
        Clear filter
      </button>
    </div>
  );
}

export function CampaignList({
  campaigns,
  statusFilter,
  clearFilter,
  isLoading,
}: {
  campaigns: CampaignRecord[];
  statusFilter: Set<CampaignStatus>;
  clearFilter: () => void;
  isLoading: boolean;
}) {
  if (statusFilter.size === 0)
    return <NoFilterSelectedEmptyPlaceholder clearFilter={clearFilter} />;

  if (isLoading) return <LoadingCampaignsEmptyPlaceholder />;

  if (campaigns.length === 0)
    return <NoCampaignsEmptyPlaceholder clearFilter={clearFilter} />;

  return (
    <div className="tw:grid-cols-1 tw:@[1024px]:grid-cols-2 tw:@[1800px]:grid-cols-3 tw:grid tw:gap-4 tw:py-4">
      {campaigns.map((campaign) => (
        <CampaignListCard key={campaign.id} campaign={campaign} />
      ))}
    </div>
  );
}

function fillFilterIcon(hasDefaultSelection: boolean, theme: "dark" | "light") {
  if (theme == "dark") return hasDefaultSelection ? "black" : "white";
  else return hasDefaultSelection ? "white" : "black";
}

export function CampaignsPage() {
  const navigate = useNavigate();
  const realm = useAuthStore((s) => s.realm);
  const [statusFilter, setStatusFilter] = useState(
    new Set(campaignStatusFilterDefaultSelection),
  );
  const result = useCampaigns({
    statuses: Array.from(statusFilter),
  });

  const campaigns = result.data ?? [];

  const clearStatusFilter = () => {
    setStatusFilter(new Set(campaignStatusFilterDefaultSelection));
  };

  const theme = useTheme();

  return (
    <main className="tw:@container tw:flex tw:min-w-0 tw:flex-col tw:px-6 tw:py-8 sm:tw:px-8">
      <CardHeader className="tw:mb-3 tw:px-0 tw:pt-0 tw:relative tw:flex tw:flex-col tw:gap-4 tw:pr-0 sm:tw:pr-36">
        <div className="tw:space-y-1">
          <CardTitle>
            <h1 className="tw:text-2xl tw:font-semibold">Campaigns</h1>
          </CardTitle>
          <CardDescription className="tw:max-w-2xl">
            Monitor every draft, scheduled launch, and live placement across
            your Blaze Ads workspace.
          </CardDescription>
        </div>
        <div></div>

        <CardAction className="tw:absolute tw:flex tw:right-0 tw:top-0 tw:gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="tw:group">
                <FilterIcon
                  className={
                    statusFilter.size > 0
                      ? "tw:group-hover:fill-blue-500"
                      : "tw:fill-transparent"
                  }
                  fill={fillFilterIcon(
                    statusFilterHasDefaultSelection(statusFilter),
                    theme.resolvedTheme,
                  )}
                />
                {formatFilterButtonLabel(statusFilter)}
              </Button>
            </DropdownMenuTrigger>
            <CampaignStatusFilterMenuContent
              checkedStatuses={statusFilter}
              setCheckedStatuses={setStatusFilter}
            />
          </DropdownMenu>
          {realm === "staff" ? (
            <Button
              className="tw:cursor-pointer"
              onClick={() => navigate("/campaigns/new")}
            >
              New campaign
            </Button>
          ) : null}
        </CardAction>
      </CardHeader>

      <StatusFilterBadges
        statusFilter={statusFilter}
        clearStatuses={clearStatusFilter}
      />

      <CampaignList
        campaigns={campaigns}
        statusFilter={statusFilter}
        clearFilter={clearStatusFilter}
        isLoading={result.isFetching}
      />
    </main>
  );
}
